import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { axFetch } from '../lib/utils';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string) => void;
  logout: () => void;
  checkAuth: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Check authentication status
  const checkAuth = async (): Promise<boolean> => {
    const token = localStorage.getItem('authToken');

    if (!token) {
      return false;
    }

    try {
      // Use the enhanced axFetch which handles token refresh automatically
      const response = await axFetch('POST', 'validate-token', {});

      if (response.ok) {
        return true;
      } else {
        // Token is invalid, clear it
        localStorage.removeItem('authToken');
        return false;
      }
    } catch (error) {
      console.error('Auth check error:', error);
      localStorage.removeItem('authToken');
      return false;
    }
  };

  // Login function
  const login = (token: string) => {
    localStorage.setItem('authToken', token);
    setIsAuthenticated(true);

    // Dispatch custom event for backward compatibility
    window.dispatchEvent(new CustomEvent('authStateChanged', {
      detail: { isAuthenticated: true }
    }));
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('authToken');
    setIsAuthenticated(false);

    // Dispatch custom event for backward compatibility
    window.dispatchEvent(new CustomEvent('authStateChanged', {
      detail: { isAuthenticated: false }
    }));

    // Navigate to landing page
    navigate('/');
  };

  // Initial authentication check
  useEffect(() => {
    const initializeAuth = async () => {
      setIsLoading(true);

      const token = localStorage.getItem('authToken');
      if (!token) {
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      const isValid = await checkAuth();
      setIsAuthenticated(isValid);
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  // Listen for storage changes and custom auth events
  useEffect(() => {
    const handleStorageChange = () => {
      const token = localStorage.getItem('authToken');
      setIsAuthenticated(!!token);
    };

    const handleAuthStateChange = (event: CustomEvent) => {
      setIsAuthenticated(event.detail.isAuthenticated);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('authStateChanged', handleAuthStateChange as EventListener);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authStateChanged', handleAuthStateChange as EventListener);
    };
  }, []);

  // Global authentication guard
  useEffect(() => {
    // Public routes that don't require authentication
    const publicRoutes = [
      '/',
      '/excel',
      '/new',
      '/download',
      '/support',
      '/enterprise',
      '/subscription-success',
      '/subscription-cancel',
      '/privacy-policy',
      '/terms-of-service',
      '/eula'
    ];

    // Don't redirect if we're on a public route or if still loading
    if (publicRoutes.includes(location.pathname) || isLoading) {
      return;
    }
    // If not authenticated and not on a public route, redirect to landing page
    if (!isAuthenticated && !isLoading) {
      console.log('🔒 Not authenticated, redirecting to landing page');
      navigate('/');
    }
  }, [isAuthenticated, isLoading, location.pathname, navigate]);

  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    login,
    logout,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
